import { apiClient } from '@/api/client';
import type {
  NotificationHistoryItem,
  NotificationItem,
  NotificationQuery,
  NotificationsSummary,
  UpdateNotificationStatusRequest,
} from '@/types/notification';

/** API client for the notifications and critical-items feed. */
export const notificationsApi = {
  /**
   * List notifications matching optional filters.
   * @param query - Optional filters (limit, status, unread, entity/category, search text).
   * @returns Matching notification items.
   */
  async list(query: NotificationQuery = {}): Promise<NotificationItem[]> {
    const { data } = await apiClient.get<NotificationItem[]>('/api/notifications', {
      params: {
        limit: query.limit ?? 20,
        status: query.status,
        onlyUnread: query.onlyUnread,
        entityType: query.entityType,
        assetCategory: query.assetCategory,
        search: query.search,
      },
    });
    return data;
  },

  /** Fetch aggregate unread/critical notification counts for the current user. */
  async summary(): Promise<NotificationsSummary> {
    const { data } = await apiClient.get<NotificationsSummary>('/api/notifications/summary');
    return data;
  },

  /**
   * Mark a notification event as seen by the current user.
   * @param notificationEventId - Identifier of the notification event.
   */
  async markSeen(notificationEventId: number): Promise<void> {
    await apiClient.post(`/api/notifications/${notificationEventId}/seen`);
  },

  /**
   * Mark a critical item as "in order" (resolved/no action needed).
   * @param criticalItemId - Identifier of the critical item.
   * @param note - Optional free-text note; blank/whitespace is sent as null.
   */
  async markInOrder(criticalItemId: number, note?: string): Promise<void> {
    await apiClient.post(`/api/notifications/critical/${criticalItemId}/in-order`, {
      note: note?.trim() || null,
    });
  },

  /**
   * Update the workflow status of a critical item.
   * @param criticalItemId - Identifier of the critical item.
   * @param request - New status and optional note.
   */
  async updateStatus(criticalItemId: number, request: UpdateNotificationStatusRequest): Promise<void> {
    await apiClient.post(`/api/notifications/critical/${criticalItemId}/status`, {
      status: request.status,
      note: request.note?.trim() || null,
    });
  },

  /**
   * Add a comment to a critical item's history.
   * @param criticalItemId - Identifier of the critical item.
   * @param comment - Comment text (trimmed before sending).
   */
  async addComment(criticalItemId: number, comment: string): Promise<void> {
    await apiClient.post(`/api/notifications/critical/${criticalItemId}/comment`, {
      comment: comment.trim(),
    });
  },

  /**
   * Fetch the audit/status history for a critical item.
   * @param criticalItemId - Identifier of the critical item.
   * @returns The item's history entries, ordered as returned by the API.
   */
  async history(criticalItemId: number): Promise<NotificationHistoryItem[]> {
    const { data } = await apiClient.get<NotificationHistoryItem[]>(`/api/notifications/critical/${criticalItemId}/history`);
    return data;
  },
};
